import { useEffect, useId, useRef, useState } from "react";
import type { Currency, LineItem, Participant, UpdateLineRequest } from "@/api/types";
import { MoneyAmount } from "./MoneyAmount";
import { ParticipantChip } from "./ParticipantChip";
import { IconButton } from "./IconButton";
import "./receipt-item-row.css";

export interface ReceiptItemRowProps {
  line: LineItem;
  ccy: Currency;
  participants: ReadonlyArray<Participant>;
  optedOutPids: ReadonlySet<string>;
  onToggleConsumer: (lineId: string, participantId: string, currentlyTagged: boolean) => void;
  onToggleSharedByAll?: (lineId: string, nextValue: boolean) => void;
  onEditLine?: (lineId: string, patch: UpdateLineRequest) => void;
  onRequestDeleteLine?: (lineId: string) => void;
  readOnly?: boolean;
}

/**
 * Controlled inputs with local draft state — guards against focus loss
 * when an optimistic-version bump from a sibling PATCH lands while the
 * user is typing. We sync from props only when the input is NOT the
 * active element, so server echoes don't yank the cursor mid-edit.
 */
function useSyncedDraft(
  source: string,
  inputRef: React.RefObject<HTMLInputElement | null>,
): [string, React.Dispatch<React.SetStateAction<string>>] {
  const [draft, setDraft] = useState(source);
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setDraft(source);
  }, [source, inputRef]);
  return [draft, setDraft];
}

export function ReceiptItemRow({
  line,
  ccy,
  participants,
  optedOutPids,
  onToggleConsumer,
  onToggleSharedByAll,
  onEditLine,
  onRequestDeleteLine,
  readOnly,
}: ReceiptItemRowProps) {
  const nameId = useId();
  const qtyId = useId();
  const priceId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const qtyRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);

  const [nameDraft, setNameDraft] = useSyncedDraft(line.name, nameRef);
  const [qtyDraft, setQtyDraft] = useSyncedDraft(String(line.quantity), qtyRef);
  const [priceDraft, setPriceDraft] = useSyncedDraft(String(line.unit_price), priceRef);

  const taggedPids = new Set((line.consumers ?? []).map((c) => c.participant_id));
  const hasTags = taggedPids.size > 0;
  // «Усі» is a select-all SHORTCUT, not a lock (AC-13.1..13.6). The chip strip
  // stays individually tappable on every line — tap a tagged chip to remove
  // someone, an untagged chip to add them. The «Усі» toggle is "pressed" only
  // when every eligible (non-opted-out) participant is in the split; tapping it
  // selects all / clears all. A late joiner is simply an untagged chip (honest:
  // not in the split, one tap to add) — never a struck-through fake consumer.
  const eligible = participants.filter((p) => !optedOutPids.has(p.participant_id));
  const allTagged =
    eligible.length > 0 && eligible.every((p) => taggedPids.has(p.participant_id));
  const showTranslated =
    line.translated_name &&
    line.translated_name.trim() !== "" &&
    line.translated_name !== line.name;
  const isPending = line.line_id.startsWith("__pending-");

  function commitName(value: string) {
    const trimmed = value.trim();
    if (!onEditLine || trimmed === "" || trimmed === line.name) {
      // revert empty/unchanged drafts back to the server value
      setNameDraft(line.name);
      return;
    }
    onEditLine(line.line_id, { name: trimmed, version: line.version });
  }
  function commitQty(value: string) {
    if (!onEditLine) return;
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0 || n === line.quantity) {
      setQtyDraft(String(line.quantity));
      return;
    }
    onEditLine(line.line_id, { quantity: n, version: line.version });
  }
  function commitPrice(value: string) {
    if (!onEditLine) return;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0 || n === line.unit_price) {
      setPriceDraft(String(line.unit_price));
      return;
    }
    onEditLine(line.line_id, { unit_price: n, version: line.version });
  }

  return (
    <li className="wp-line" data-testid="receipt-line">
      <div className="wp-line__head">
        <div className="wp-line__title">
          {readOnly ? (
            <span className="wp-line__name">{line.name}</span>
          ) : (
            <>
              <label htmlFor={nameId} className="visually-hidden">
                Назва позиції
              </label>
              <input
                id={nameId}
                ref={nameRef}
                className="wp-line__name-input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={(e) => commitName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                disabled={isPending}
                data-testid="line-name-input"
                aria-label="Назва позиції"
              />
            </>
          )}
          {showTranslated ? (
            <span className="wp-line__translated">{line.translated_name}</span>
          ) : null}
        </div>
        <MoneyAmount value={line.line_total} ccy={ccy} size="body" />
      </div>

      {readOnly ? (
        <p className="wp-line__qty">
          {line.quantity} ×{" "}
          <MoneyAmount value={line.unit_price} ccy={ccy} size="caption" /> ={" "}
          <MoneyAmount value={line.line_total} ccy={ccy} size="caption" />
        </p>
      ) : (
        <p className="wp-line__qty wp-line__qty--editable">
          <label htmlFor={qtyId} className="visually-hidden">
            Кількість
          </label>
          <input
            id={qtyId}
            ref={qtyRef}
            type="number"
            inputMode="decimal"
            step="any"
            min={0}
            className="wp-line__qty-input"
            value={qtyDraft}
            onChange={(e) => setQtyDraft(e.target.value)}
            onBlur={(e) => commitQty(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            disabled={isPending}
            data-testid="line-qty-input"
            aria-label="Кількість"
          />
          <span className="wp-line__qty-sep" aria-hidden="true">
            ×
          </span>
          <label htmlFor={priceId} className="visually-hidden">
            Ціна за одиницю
          </label>
          <input
            id={priceId}
            ref={priceRef}
            type="number"
            inputMode="decimal"
            step="any"
            min={0}
            className="wp-line__price-input"
            value={priceDraft}
            onChange={(e) => setPriceDraft(e.target.value)}
            onBlur={(e) => commitPrice(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            disabled={isPending}
            data-testid="line-price-input"
            aria-label="Ціна за одиницю"
          />
          <span className="wp-line__qty-ccy" aria-hidden="true">
            {ccy}
          </span>
          <span className="wp-line__qty-sep" aria-hidden="true">
            =
          </span>
          <MoneyAmount value={line.line_total} ccy={ccy} size="caption" />
        </p>
      )}

      {!hasTags ? (
        <p className="wp-line__warn">⚠ Без тегів — піде на платника</p>
      ) : null}
      <div className="wp-line__strip">
        <button
          type="button"
          className={["wp-line__shared", allTagged ? "wp-line__shared--on" : null]
            .filter(Boolean)
            .join(" ")}
          aria-pressed={allTagged}
          disabled={readOnly || isPending}
          onClick={() => onToggleSharedByAll?.(line.line_id, !allTagged)}
        >
          Усі
        </button>
        {/* The strip stays individually tappable on every line: tap a tagged
            chip to remove that person, an untagged chip to add them. «Усі»
            above is just the select-all / clear-all shortcut. */}
        {participants.map((p) => {
          const tagged = taggedPids.has(p.participant_id);
          const optedOut = optedOutPids.has(p.participant_id);
          const consumer = (line.consumers ?? []).find(
            (c) => c.participant_id === p.participant_id,
          );
          return (
            <ParticipantChip
              key={p.participant_id}
              participant={p}
              tagged={tagged}
              quantity={consumer?.quantity ?? null}
              disabled={optedOut || readOnly || isPending}
              onClick={() =>
                !readOnly &&
                !optedOut &&
                !isPending &&
                onToggleConsumer(line.line_id, p.participant_id, tagged)
              }
            />
          );
        })}
      </div>

      {!readOnly && onRequestDeleteLine ? (
        <div className="wp-line__footer">
          <IconButton
            aria-label={`Видалити позицію «${line.name}»`}
            onClick={() => onRequestDeleteLine(line.line_id)}
            disabled={isPending}
            className="wp-line__delete"
            data-testid="line-delete"
          >
            🗑
          </IconButton>
        </div>
      ) : null}
    </li>
  );
}
